import { useState, type FormEvent } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { CheckCircle2, ChevronRight, Send } from 'lucide-react';
import { db, firebaseReady } from '../lib/firebase';
import { useSiteData } from '../context/SiteDataContext';
import Reveal from '../components/Reveal';
import ServiceIcon from '../components/ServiceIcon';
import { money } from '../lib/format';

export default function CustomWork() {
  const { services, settings } = useSiteData();
  const [form, setForm] = useState({
    serviceName: '',
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    details: '',
    budget: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!firebaseReady) {
      window.location.href = `mailto:${settings.email}?subject=Quote Request: ${form.serviceName}&body=${encodeURIComponent(
        `Name: ${form.name}\nPhone: ${form.phone}\nVehicle: ${form.vehicle}\n\n${form.details}`,
      )}`;
      return;
    }
    setStatus('sending');
    try {
      await addDoc(collection(db(), 'quotes'), {
        createdAt: Date.now(),
        status: 'new',
        serviceName: form.serviceName || 'General custom work',
        customer: { name: form.name, email: form.email, phone: form.phone },
        vehicle: form.vehicle,
        details: form.details,
        budget: form.budget,
        adminNotes: '',
      });
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div>
      <section className="relative border-b border-white/[0.06]">
        <div className="hero-grid absolute inset-0" />
        <div className="container-x relative py-16 text-center sm:py-20">
          <Reveal>
            <span className="eyebrow">Custom Shop</span>
            <h1 className="heading-xl mt-4">
              Make It <span className="gradient-text">Yours</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-zinc-400">
              Audio that hits, lighting that turns heads, wraps and protection done right. Every
              custom job starts with a free, no-pressure quote.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-x py-16">
        <div className="grid gap-6 md:grid-cols-2">
          {services.map((svc, i) => (
            <Reveal key={svc.id} delay={(i % 2) * 100}>
              <div className="card glass-hover flex h-full flex-col p-7">
                <div className="flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-accent-600 to-sky-500 text-white">
                    <ServiceIcon name={svc.icon} size={24} />
                  </div>
                  {svc.comingSoon && (
                    <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-300">
                      Coming Soon
                    </span>
                  )}
                </div>
                <h2 className="mt-5 font-display text-2xl font-bold text-white">{svc.name}</h2>
                <p className="mt-2 leading-relaxed text-zinc-400">{svc.description}</p>
                <ul className="mt-4 flex-1 space-y-2">
                  {svc.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-zinc-300">
                      <ChevronRight size={15} className="mt-0.5 shrink-0 text-accent-400" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex items-center justify-between border-t border-white/[0.06] pt-5">
                  {svc.priceFrom != null ? (
                    <span className="text-sm text-zinc-400">
                      Starting at <span className="font-display text-lg font-bold text-accent-300">{money(svc.priceFrom)}</span>
                    </span>
                  ) : (
                    <span className="text-sm text-zinc-400">Custom quoted</span>
                  )}
                  <a
                    href="#quote"
                    onClick={() => setForm((f) => ({ ...f, serviceName: svc.name }))}
                    className="btn-ghost !px-4 !py-2 text-sm"
                  >
                    Get a Quote
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="quote" className="border-t border-white/[0.06] bg-night-900/60 py-16">
        <div className="container-x max-w-3xl">
          <Reveal>
            <div className="text-center">
              <span className="eyebrow">Free Quote</span>
              <h2 className="heading-lg mt-3">Tell Us What You're Dreaming Up</h2>
              <p className="mx-auto mt-3 max-w-xl text-zinc-400">
                Describe the project and we'll get back to you with options and honest pricing —
                usually within one business day.
              </p>
            </div>
          </Reveal>

          {status === 'sent' ? (
            <Reveal>
              <div className="card mt-10 flex flex-col items-center gap-3 p-10 text-center">
                <CheckCircle2 size={44} className="text-emerald-400" />
                <h3 className="font-display text-xl font-bold text-white">Quote request received!</h3>
                <p className="text-zinc-400">
                  Thanks {form.name.split(' ')[0]} — we'll reach out at {form.phone || form.email} shortly.
                </p>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={100}>
              <form onSubmit={submit} className="card mt-10 grid gap-5 p-7 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="label">Service</label>
                  <select className="input" value={form.serviceName} onChange={set('serviceName')} required>
                    <option value="" disabled>Select a service…</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                    <option value="Something else">Something else</option>
                  </select>
                </div>
                <div>
                  <label className="label">Your Name</label>
                  <input className="input" value={form.name} onChange={set('name')} required placeholder="Jane Driver" />
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input className="input" type="tel" value={form.phone} onChange={set('phone')} required placeholder="(555) 555-0123" />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input className="input" type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" />
                </div>
                <div>
                  <label className="label">Vehicle</label>
                  <input className="input" value={form.vehicle} onChange={set('vehicle')} required placeholder="2020 Mustang GT" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label">Project Details</label>
                  <textarea className="input min-h-28" value={form.details} onChange={set('details')} required placeholder="What are you looking to do? Any specific brands or looks in mind?" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label">Budget Range (optional)</label>
                  <select className="input" value={form.budget} onChange={set('budget')}>
                    <option value="">Prefer not to say</option>
                    <option>Under $500</option>
                    <option>$500 – $1,000</option>
                    <option>$1,000 – $2,500</option>
                    <option>$2,500 – $5,000</option>
                    <option>$5,000+</option>
                  </select>
                </div>
                {status === 'error' && (
                  <p className="text-sm text-red-400 sm:col-span-2">
                    Something went wrong sending your request — please try again or call us at {settings.phone}.
                  </p>
                )}
                <div className="sm:col-span-2">
                  <button type="submit" disabled={status === 'sending'} className="btn-primary w-full disabled:opacity-60">
                    {status === 'sending' ? 'Sending…' : <>Request My Free Quote <Send size={16} /></>}
                  </button>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
